(() => {
  'use strict';

  /**
   * Pipeline ImageTask — identity = fileOriginalId + mockup props.
   * Tracks: status.mockup | status.saveToS3 (no pillow3d alias).
   * Loads as window.ImageTaskModule on shaped pages (instead of image-task-module.js).
   */
  const ImageTaskModule = (() => {
    const STATUS = {
      PENDING: 'pending',
      SUCCESS: 'success',
      ERROR: 'error',
      SKIP: 'skip'
    };

    const identityProps = (props) => {
      if (props == null || typeof props !== 'object') return {};
      const out = {};
      for (const key of Object.keys(props)) {
        const value = props[key];
        if (value == null || value === '') continue;
        out[key] = String(value);
      }
      return out;
    };

    const propsIdentitySuffix = (props) => {
      const normalized = identityProps(props);
      const keys = Object.keys(normalized).sort();
      if (!keys.length) return '';
      return keys.map((k) => normalized[k]).join('_');
    };

    const generateOriginalFileId = (name, size, lastModified) => {
      if (!name) return null;
      if (typeof size !== 'number' || typeof lastModified !== 'number') return null;
      return `${name}_${size}_${lastModified}`;
    };

    const generateTaskId = (originalFileId, props) => {
      const suffix = propsIdentitySuffix(props);
      if (originalFileId && suffix) {
        return `${originalFileId}_${suffix}`;
      }
      return `task-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    const extractOriginalFileIdFromTaskId = (taskId, props) => {
      if (!taskId) return null;
      const suffix = propsIdentitySuffix(props);
      if (!suffix) return null;
      const ending = `_${suffix}`;
      return taskId.endsWith(ending) ? taskId.slice(0, -ending.length) : null;
    };

    /**
     * @param {string|null} originalFileId
     * @param {Object} props - mockup identity props from init mockupProps
     * @param {string|null} historyId
     */
    const create = (originalFileId, props = {}, historyId = null) => {
      const normalized = identityProps(props);
      const id = generateTaskId(originalFileId, normalized);

      return {
        id,
        fileOriginalId: originalFileId || null,
        props: { ...normalized },
        color: normalized.color || null,
        historyId: historyId || null,
        fileUrl: null,
        s3Url: null,
        originalS3Url: null,
        pillowData: null,
        preview: null,
        dataUrl: null,
        fileType: null,
        fileName: null,
        extension: null,
        status: {
          mockup: STATUS.PENDING,
          saveToS3: STATUS.PENDING
        },
        error: {
          event: null,
          type: null,
          message: null
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
    };

    const createEmpty = () => ({
      id: null,
      fileOriginalId: null,
      props: {},
      color: null,
      historyId: null,
      fileUrl: null,
      s3Url: null,
      originalS3Url: null,
      pillowData: null,
      preview: null,
      dataUrl: null,
      fileType: null,
      fileName: null,
      extension: null,
      status: {
        mockup: STATUS.PENDING,
        saveToS3: STATUS.PENDING
      },
      error: {
        event: null,
        type: null,
        message: null
      },
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    const update = (task, updates = {}, taskId = null) => {
      if (!task) return null;
      if (taskId && task.id !== taskId) return null;

      const next = { ...updates };

      if (next.props !== undefined) {
        const props = identityProps(next.props);
        next.props = props;
        next.color = props.color || null;
        if (task.fileOriginalId) {
          next.id = generateTaskId(task.fileOriginalId, props);
        }
        next.historyId = null;
      }

      return {
        ...task,
        ...next,
        updatedAt: new Date().toISOString()
      };
    };

    const updateForPropsChange = (task, props) => {
      if (!task || !props || typeof props !== 'object') return null;
      const merged = {
        ...identityProps(task.props),
        ...identityProps(props)
      };
      return update(task, {
        props: merged,
        pillowData: null,
        preview: null
      }, task.id);
    };

    const validateTaskId = (task, id) => !!(task && id && task.id === id);

    const hashBlob = async (blob) => {
      if (!blob || typeof crypto === 'undefined' || !crypto.subtle?.digest) {
        return null;
      }
      try {
        const buffer = await blob.arrayBuffer();
        const digest = await crypto.subtle.digest('SHA-256', buffer);
        const hex = Array.from(new Uint8Array(digest), (b) =>
          b.toString(16).padStart(2, '0')
        ).join('');
        return `h_${hex}`;
      } catch (e) {
        console.warn('[ImageTaskModule] hashBlob failed', e);
        return null;
      }
    };

    const generateFileOriginalId = (file, contentHash = null) => {
      if (contentHash && typeof contentHash === 'string') {
        return contentHash.startsWith('h_') ? contentHash : `h_${contentHash}`;
      }
      if (file?.name && typeof file.size === 'number' && typeof file.lastModified === 'number') {
        return generateOriginalFileId(file.name, file.size, file.lastModified);
      }
      return null;
    };

    return {
      create,
      createEmpty,
      update,
      updateForPropsChange,
      validateTaskId,
      generateOriginalFileId,
      generateTaskId,
      extractOriginalFileIdFromTaskId,
      generateFileOriginalId,
      hashBlob,
      identityProps,
      propsIdentitySuffix,
      STATUS
    };
  })();

  if (typeof window !== 'undefined') {
    window.ImageTaskModule = ImageTaskModule;
  }
})();
